import React, { useState, useEffect } from "react";
import { useDeal } from "../lib/store";
import { dealsApi } from "../lib/api";
import { Card } from "../components/shared";
import { STAGES, verdict, fmt$, runRateEbitda, isRunRateView } from "../lib/model";
import { Trash2, Users, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function Pipeline() {
  const { M, setField, dealId, setDealId, dealName, setDealName, loadModel } = useDeal();
  const [deals, setDeals] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = async () => setDeals(await dealsApi.list());
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return "—"; }
  };

  const saveCurrent = async () => {
    const payload = { name: dealName || M.dealName, model: M, verdict: verdict(M) };
    if (dealId) { await dealsApi.update(dealId, payload); toast.success("Deal updated"); }
    else { const d = await dealsApi.create({ ...payload, stage: "Underwriting" }); setDealId(d.id); toast.success("Deal saved to pipeline"); }
    load();
  };

  const move = async (deal, dir) => {
    const idx = STAGES.indexOf(deal.stage);
    const ni = Math.max(0, Math.min(STAGES.length - 1, idx + dir));
    await dealsApi.update(deal.id, { stage: STAGES[ni] });
    load();
  };
  const remove = async (id) => { await dealsApi.remove(id); if (id === dealId) setDealId(null); load(); };
  const open = (deal) => { loadModel(deal.model, deal.id, deal.name, deal.market); toast.success(`Loaded ${deal.name}`); };

  const dot = (v) => (v === "GO" ? "go" : v === "PASS" ? "pass" : "amber");

  return (
    <div className="page" data-testid="page-pipeline">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div><div className="page-title">Deal Pipeline</div><div className="page-sub">Move deals through stages · saved to database</div></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input className="inp" style={{ width: 180 }} value={dealName} onChange={(e) => setDealName(e.target.value)} placeholder="Deal name" data-testid="pipeline-dealname" />
          <input className="inp" style={{ width: 150 }} value={M.analystName || ""} onChange={(e) => setField("analystName", e.target.value)} placeholder="Analyst" data-testid="pipeline-analyst" />
          <button className="btn btn-primary" onClick={saveCurrent} data-testid="save-deal">{dealId ? "Update Current Deal" : "Save Current Deal"}</button>
        </div>
      </div>

      <div className="pipe" data-testid="pipeline-board">
        {STAGES.map((stage) => {
          const items = deals.filter((d) => d.stage === stage);
          return (
            <div className="pipe-col" key={stage} data-testid={`stage-${stage}`}>
              <h4>{stage}<span className="mono" style={{ color: "var(--text3)" }}>{items.length}</span></h4>
              <div className="pipe-body">
                {items.map((d) => (
                  <div className="pipe-card" key={d.id} data-testid="pipe-card">
                    <div className="nm"><span className={`pulse-dot ${dot(d.verdict)}`} />{d.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>{fmt$(d.model?.askPrice || 0)} · {d.verdict || "—"}</div>
                    <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 2, display: "flex", justifyContent: "space-between", gap: 6 }} data-testid="card-ebitda">
                      <span>{d.model && isRunRateView(d.model) ? "Run-rate EBITDA" : "Stated EBITDA"}</span>
                      <b className="mono" style={{ color: "var(--text2)" }}>{fmt$(d.model ? runRateEbitda(d.model) : 0)}</b>
                    </div>
                    <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4, display: "flex", justifyContent: "space-between", gap: 6 }}>
                      <span data-testid="card-analyst">{d.model?.analystName || "XYZ Ventures"}</span>
                      <span className="mono" data-testid="card-date">{fmtDate(d.updated_at || d.created_at)}</span>
                    </div>
                    {d.prospects?.length > 0 && (
                      <div style={{ marginTop: 6 }} data-testid="card-shortlist">
                        <button className="btn" style={{ padding: "3px 8px", fontSize: 10.5, width: "100%" }} onClick={() => setExpanded((e) => ({ ...e, [d.id]: !e[d.id] }))} data-testid={`toggle-shortlist-${d.id}`}>
                          <Users size={11} style={{ marginRight: 4 }} />{d.prospects.length} investor{d.prospects.length > 1 ? "s" : ""} shortlisted {expanded[d.id] ? "▲" : "▼"}
                        </button>
                        {expanded[d.id] && (
                          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                            {d.prospects.map((p, pi) => (
                              <div key={pi} style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 4, padding: "5px 7px", fontSize: 10.5 }} data-testid="shortlist-item">
                                <div style={{ fontWeight: 600, display: "flex", justifyContent: "space-between", gap: 6 }}>
                                  <span>{p.name}</span>
                                  {typeof p.fitScore === "number" && <span className="mono" style={{ color: "var(--accent)" }}>{p.fitScore}</span>}
                                </div>
                                <div style={{ color: "var(--text3)" }}>{p.type}{p.email ? ` · ${p.email}` : ""}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {d.lenderProspects?.length > 0 && (
                      <div style={{ marginTop: 6 }} data-testid="card-lender-shortlist">
                        <button className="btn" style={{ padding: "3px 8px", fontSize: 10.5, width: "100%" }} onClick={() => setExpanded((e) => ({ ...e, [d.id + "_l"]: !e[d.id + "_l"] }))} data-testid={`toggle-lenders-${d.id}`}>
                          <Landmark size={11} style={{ marginRight: 4 }} />{d.lenderProspects.length} lender{d.lenderProspects.length > 1 ? "s" : ""} shortlisted {expanded[d.id + "_l"] ? "▲" : "▼"}
                        </button>
                        {expanded[d.id + "_l"] && (
                          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                            {d.lenderProspects.map((p, pi) => (
                              <div key={pi} style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 4, padding: "5px 7px", fontSize: 10.5 }} data-testid="lender-shortlist-item">
                                <div style={{ fontWeight: 600, display: "flex", justifyContent: "space-between", gap: 6 }}>
                                  <span>{p.name}</span>
                                  {typeof p.fitScore === "number" && <span className="mono" style={{ color: "var(--accent)" }}>{p.fitScore}</span>}
                                </div>
                                <div style={{ color: "var(--text3)" }}>{p.type}{p.email ? ` · ${p.email}` : ""}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mv">
                      <button className="btn" onClick={() => move(d, -1)} disabled={STAGES.indexOf(d.stage) === 0}>◀</button>
                      <button className="btn" onClick={() => move(d, 1)} disabled={STAGES.indexOf(d.stage) === STAGES.length - 1}>▶</button>
                      <button className="btn" onClick={() => open(d)} data-testid={`open-${d.id}`}>Open</button>
                      <button className="btn" onClick={() => remove(d.id)} data-testid={`delete-${d.id}`}><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
